import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Star } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LogoMark } from "@/components/LogoMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Recensione() {
  const params = new URLSearchParams(window.location.search);
  const unlockId = params.get("u") || "";
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [testo, setTesto] = useState("");
  const [nome, setNome] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!unlockId) {
      toast.error("Link non valido: manca il riferimento allo sblocco");
      return;
    }
    if (rating === 0) {
      toast.error("Seleziona un voto in stelle");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/reviews`, { unlock_id: unlockId, rating, testo, nome });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'invio, riprova");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-5 py-16">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <div className="flex flex-col items-center mb-8">
          <LogoMark size={48} />
          <p className="text-xs text-slate-500 mt-3">La casa cerca te! · powered by Casamatch.org</p>
        </div>

        {done ? (
          <div className="bg-white rounded-3xl border border-slate-200 shadow-[0_20px_60px_rgba(10,25,47,0.08)] p-10 text-center" data-testid="review-success">
            <CheckCircle2 className="w-12 h-12 text-[#10B981] mx-auto mb-4" />
            <h1 className="font-display text-2xl font-extrabold text-[#0A192F]">Grazie!</h1>
            <p className="text-sm text-slate-500 mt-2 leading-relaxed">
              La tua recensione ci aiuta a migliorare e dà fiducia a chi arriva dopo di te.
            </p>
            <a href="/" className="inline-block mt-6 text-sm font-bold text-[#10B981] hover:underline">
              ← Torna alla home
            </a>
          </div>
        ) : (
          <form
            onSubmit={submit}
            data-testid="review-form"
            className="bg-white rounded-3xl border border-slate-200 shadow-[0_20px_60px_rgba(10,25,47,0.08)] p-8 space-y-6"
          >
            <div className="text-center">
              <h1 className="font-display text-2xl font-extrabold text-[#0A192F]">Com'è andata?</h1>
              <p className="text-sm text-slate-500 mt-2">Hai sbloccato un contatto: raccontaci la tua esperienza in 30 secondi.</p>
            </div>

            <div className="flex justify-center gap-2" data-testid="review-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`review-star-${n}`}
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  className="transition-transform duration-150 hover:scale-110"
                >
                  <Star
                    className={`w-9 h-9 ${
                      n <= (hover || rating) ? "fill-[#10B981] text-[#10B981]" : "text-slate-300"
                    }`}
                  />
                </button>
              ))}
            </div>

            <div className="space-y-2">
              <Label htmlFor="rev-testo" className="text-sm font-semibold text-[#0A192F]">La tua esperienza</Label>
              <Textarea
                id="rev-testo"
                data-testid="review-testo"
                required
                value={testo}
                onChange={(e) => setTesto(e.target.value)}
                placeholder="Es. Contatto vero, ho visitato l'immobile in 3 giorni..."
                className="rounded-xl min-h-[100px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rev-nome" className="text-sm font-semibold text-[#0A192F]">
                Nome o agenzia <span className="font-normal text-slate-400">(facoltativo)</span>
              </Label>
              <Input
                id="rev-nome"
                data-testid="review-nome"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Es. Immobiliare Salento"
                className="h-12 rounded-xl"
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              data-testid="review-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#10B981] text-white font-extrabold py-4 hover:bg-[#059669] transition-colors disabled:opacity-60"
            >
              {sending ? "Invio..." : "Invia la recensione"}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
